import { CheckedAddress, Percentage } from '@sparkdotfi/common-universal'
import { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { zeroAddress } from 'viem'

import { ActionSettingsSchema, SlippageInputType } from './form'
import { normalizeFormValues } from './normalizeFormValues'
import { UseSlippageFormResult, useSlippageForm } from './useSlippageForm'
import { useActionsSettings } from '@/domain/state'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'

export interface UseSettingsDialogResult {
  batchTransactionsSettings: {
    preferTransactionsBatching: boolean
    togglePreferTransactionsBatching: () => void
    accountAddress: CheckedAddress
  }
  permitsSettings: {
    preferPermits: boolean
    togglePreferPermits: () => void
  }
  slippageSettings: {
    form: UseFormReturn<ActionSettingsSchema>
    type: SlippageInputType
    slippage: Percentage
    onSlippageChange: UseSlippageFormResult['onSlippageChange']
    error?: string
  }
  onConfirm: () => void
  onOpen: () => void
}

export function useSettingsDialog(): UseSettingsDialogResult {
  const { account: accountAddress = CheckedAddress(zeroAddress) } = useConnectedAddress({ throwOnNotConnected: false }) // stub to have a valid address
  const actionsSettingsStore = useActionsSettings()

  const [preferTransactionsBatching, setPreferTransactionsBatching] = useState(
    actionsSettingsStore.getTxBatchingPreference(accountAddress),
  )
  const [preferPermits, setPreferPermits] = useState(actionsSettingsStore.preferPermits)
  const { form, onSlippageChange, reset: resetSlippageForm } = useSlippageForm()
  const normalizedFormValues = normalizeFormValues(form.watch())

  function onConfirm(): void {
    if (form.formState.isValid) {
      actionsSettingsStore.setExchangeMaxSlippage(normalizedFormValues.slippage.value)
    }
    actionsSettingsStore.setPreferPermits(preferPermits)
    actionsSettingsStore.setTxBatchingPreference(accountAddress, preferTransactionsBatching)
  }

  function onOpen(): void {
    resetSlippageForm()
    setPreferPermits(actionsSettingsStore.preferPermits)
    setPreferTransactionsBatching(actionsSettingsStore.getTxBatchingPreference(accountAddress))
  }

  return {
    batchTransactionsSettings: {
      preferTransactionsBatching,
      togglePreferTransactionsBatching: () => setPreferTransactionsBatching((prefer) => !prefer),
      accountAddress,
    },
    permitsSettings: {
      preferPermits,
      togglePreferPermits: () => setPreferPermits((prefer) => !prefer),
    },
    slippageSettings: {
      form,
      onSlippageChange,
      type: normalizedFormValues.slippage.type,
      error: form.formState.errors.slippage?.value?.message,
      slippage: normalizedFormValues.slippage.value,
    },
    onConfirm,
    onOpen,
  }
}
